import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { PageHeader, EmptyState } from "@/components/Shared";
import api, { formatApiError } from "@/lib/api";
import { TL, CARI_TYPE_LABEL } from "@/lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "all", label: "Tümü" },
  { key: "musteri", label: "Müşteri" },
  { key: "acente", label: "Acente" },
  { key: "tedarikci", label: "Tedarikçi" },
];

function CariForm({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", type: "musteri", phone: "", email: "", address: "", tax_number: "", notes: "" });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/cariler", form);
      toast.success("Cari hesap oluşturuldu");
      setOpen(false);
      setForm({ name: "", type: "musteri", phone: "", email: "", address: "", tax_number: "", notes: "" });
      onCreated?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="add-cari-button">
          <Plus className="w-4 h-4 mr-2" /> Yeni Cari
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">Yeni Cari Hesap</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-2">
              <Label>Ünvan / Ad</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cari-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Tür</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="cari-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="musteri">Müşteri</SelectItem>
                  <SelectItem value="acente">Acente</SelectItem>
                  <SelectItem value="tedarikci">Tedarikçi</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Telefon</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="cari-phone-input" />
            </div>
            <div className="space-y-2">
              <Label>E-posta</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cari-email-input" />
            </div>
            <div className="space-y-2">
              <Label>Vergi No</Label>
              <Input value={form.tax_number} onChange={(e) => setForm({ ...form, tax_number: e.target.value })} data-testid="cari-tax-input" />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Adres</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="cari-address-input" />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Notlar</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="cari-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button type="submit" disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="cari-submit-button">
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CariEditDialog({ cari, open, onOpenChange, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", type: "musteri", phone: "", email: "", address: "", tax_number: "", notes: "" });

  useEffect(() => {
    if (cari) {
      setForm({
        name: cari.name || "",
        type: cari.type || "musteri",
        phone: cari.phone || "",
        email: cari.email || "",
        address: cari.address || "",
        tax_number: cari.tax_number || "",
        notes: cari.notes || "",
      });
    }
  }, [cari]);

  const submit = async (e) => {
    e.preventDefault();
    if (!cari) return;
    setSaving(true);
    try {
      await api.patch(`/cariler/${cari.id}`, form);
      toast.success("Cari güncellendi");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="edit-cari-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Cari Hesabı Düzenle</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-2">
              <Label>Ünvan / Ad</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-cari-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Tür</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="edit-cari-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="musteri">Müşteri</SelectItem>
                  <SelectItem value="acente">Acente</SelectItem>
                  <SelectItem value="tedarikci">Tedarikçi</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Telefon</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="edit-cari-phone-input" />
            </div>
            <div className="space-y-2">
              <Label>E-posta</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="edit-cari-email-input" />
            </div>
            <div className="space-y-2">
              <Label>Vergi No</Label>
              <Input value={form.tax_number} onChange={(e) => setForm({ ...form, tax_number: e.target.value })} data-testid="edit-cari-tax-input" />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Adres</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="edit-cari-address-input" />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Notlar</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="edit-cari-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-edit-cari">Vazgeç</Button>
            <Button type="submit" disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="confirm-edit-cari">
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CariTable({ items, onChanged }) {
  const [pending, setPending] = useState(null); // cari being confirmed for deletion
  const [editing, setEditing] = useState(null);

  const doDelete = async () => {
    if (!pending) return;
    try {
      await api.delete(`/cariler/${pending.id}`);
      toast.success(`"${pending.name}" silindi`);
      setPending(null);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (items.length === 0) {
    return <EmptyState title="Cari bulunmuyor" hint="Yeni cari ekleyerek başlayın." testId="cariler-empty" />;
  }
  return (
    <>
    <div className="border border-border rounded-md bg-background overflow-hidden" data-testid="cariler-table">
      <table className="w-full text-sm">
        <thead className="bg-secondary/50 text-foreground/70">
          <tr>
            <th className="text-left py-3 px-4 text-[11px] uppercase tracking-wider">Ünvan</th>
            <th className="text-left py-3 px-4 text-[11px] uppercase tracking-wider">Tür</th>
            <th className="text-left py-3 px-4 text-[11px] uppercase tracking-wider">Telefon</th>
            <th className="text-right py-3 px-4 text-[11px] uppercase tracking-wider">Bakiye</th>
            <th className="text-right py-3 px-4 text-[11px] uppercase tracking-wider w-24">İşlem</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id} className="border-t border-border hover:bg-secondary/20 transition-colors" data-testid={`cari-row-${c.id}`}>
              <td className="py-3 px-4">
                <Link to={`/cariler/${c.id}`} className="text-primary hover:underline font-medium" data-testid={`cari-link-${c.id}`}>{c.name}</Link>
              </td>
              <td className="py-3 px-4">
                <span className="bg-secondary text-secondary-foreground px-2 py-0.5 rounded-sm text-xs border border-border">{CARI_TYPE_LABEL[c.type]}</span>
              </td>
              <td className="py-3 px-4 text-foreground/70">{c.phone || "—"}</td>
              <td className={`py-3 px-4 text-right tabular-nums font-medium ${c.balance > 0 ? "text-[hsl(var(--success))]" : c.balance < 0 ? "text-destructive" : "text-foreground/60"}`}>
                {TL(c.balance)}
              </td>
              <td className="py-3 px-4 text-right">
                <div className="inline-flex items-center gap-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); setEditing(c); }}
                    className="text-foreground/40 hover:text-primary transition-colors p-1"
                    data-testid={`edit-cari-${c.id}`}
                    aria-label={`${c.name} düzenle`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setPending(c); }}
                    className="text-foreground/40 hover:text-destructive transition-colors p-1"
                    data-testid={`delete-cari-${c.id}`}
                    aria-label={`${c.name} sil`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <CariEditDialog
      cari={editing}
      open={!!editing}
      onOpenChange={(o) => !o && setEditing(null)}
      onSaved={() => { setEditing(null); onChanged?.(); }}
    />

    <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
      <AlertDialogContent data-testid="delete-cari-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-heading">Cari hesabı silinsin mi?</AlertDialogTitle>
          <AlertDialogDescription>
            <span className="font-medium text-foreground">{pending?.name}</span> ve buna bağlı tüm hareketler
            (cari hareketleri ve kasa/banka üzerindeki tahsilat/ödeme kayıtları) kalıcı olarak silinecek. Bu işlem geri alınamaz.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="cancel-delete-cari">Vazgeç</AlertDialogCancel>
          <AlertDialogAction
            onClick={doDelete}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            data-testid="confirm-delete-cari"
          >
            Sil
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

export default function CarilerPage() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");

  const load = async () => {
    const { data } = await api.get("/cariler");
    setItems(data);
  };
  useEffect(() => { load().catch(console.error); }, []);

  const filtered = tab === "all" ? items : items.filter((c) => c.type === tab);

  return (
    <Layout>
      <PageHeader
        title="Cari Hesaplar"
        subtitle="Müşteri, acente ve tedarikçi hesaplarını yönetin"
        action={<CariForm onCreated={load} />}
        testId="cariler-header"
      />

      <Tabs value={tab} onValueChange={setTab} data-testid="cari-tabs">
        <TabsList className="mb-6">
          {TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`tab-${t.key}`}>{t.label}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value={tab}>
          <CariTable items={filtered} onChanged={load} />
        </TabsContent>
      </Tabs>
    </Layout>
  );
}
